import type { ChangeState, EntityChange } from '../auditLogTypes';

const STATE_TONES: Record<ChangeState, string> = {
  Added: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Modified: 'bg-amber-50 text-amber-700 ring-amber-200',
  Deleted: 'bg-red-50 text-red-700 ring-red-200',
};

/**
 * Every entity a request mutated — the Field / Original / New shape frontend reference §13
 * mandates, with the state pill and the tinted changed rows.
 *
 * Shared by the audit detail modal and the audit-log profile page, so the two can never
 * drift apart on what a change looks like. The section heading belongs to the host: the
 * modal keeps §13's uppercase chrome, the profile page uses the plain-case heading every
 * other detail surface uses.
 */
export function EntityChangeList({ changes }: { changes: EntityChange[] }) {
  if (changes.length === 0) {
    return (
      <p className="text-sm text-slate-500">No database changes were recorded for this request.</p>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {changes.map((change, index) => (
        <ChangeBlock key={`${change.entityType}-${change.key ?? index}`} change={change} />
      ))}
    </div>
  );
}

/** One mutated entity: a header with its state pill over a Field / Original / New table. */
function ChangeBlock({ change }: { change: EntityChange }) {
  // The union of both sides, so a field that only exists on one still shows.
  const fields = [
    ...new Set([
      ...Object.keys(change.originalValues ?? {}),
      ...Object.keys(change.newValues ?? {}),
    ]),
  ].sort((a, b) => a.localeCompare(b));

  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-slate-200">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <p className="min-w-0 truncate text-sm text-slate-700">
          {[change.entityType, change.display, change.key ? `[${change.key}]` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ring-1 ${STATE_TONES[change.state]}`}
        >
          {change.state}
        </span>
      </div>

      {/* Wide, monospaced content scrolls inside its own container, never the page body. */}
      <div className="modern-scrollbar overflow-x-auto">
        <table className="w-full border-collapse font-mono text-xs">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th scope="col" className="px-3 py-1.5 text-left font-semibold">
                Field
              </th>
              <th scope="col" className="px-3 py-1.5 text-left font-semibold">
                Original
              </th>
              <th scope="col" className="px-3 py-1.5 text-left font-semibold">
                New
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const before = change.originalValues?.[field] ?? null;
              const after = change.newValues?.[field] ?? null;
              const moved = before !== after;

              return (
                <tr
                  key={field}
                  className={`border-t border-slate-100 ${moved ? 'bg-amber-50/40' : ''}`}
                >
                  <td className="px-3 py-1.5 align-top text-slate-600">{field}</td>
                  <td className="px-3 py-1.5 align-top text-slate-500">
                    {before ?? <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-1.5 align-top text-slate-700">
                    {after ?? <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
